import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import cc from 'classcat';
import { addBetSlipEvent } from 'utils/betslip/betslip';
import { EIconSize } from 'utils/constants/app/ui';
import { createOddType } from 'utils/sportsbook/create-odd-type';
import {
  IScoreOptions,
  orderOptions
} from 'utils/sportsbook/order-correct-score-market';
import { Event, GameInfo, Market } from 'interfaces/sportsbook-data-levels';
import { Odds } from 'components/Odds';
import { useSportsbookConfigs } from 'hooks/sportsbook/useSportsbookConfigs';
import { useOddChange } from 'hooks/useOddChange';
import { useSbRouteMatch } from 'newhooks/generic/useSbRouteMatch';
import { useSportName } from 'providers/SportDataProvider';
import { updateEventAndGameIds } from 'store/actions';
import {
  getEditBet,
  getEventIds,
  getQuickBetEnabled
} from 'store/selectors/betslip';
import { getSportIdByAlias } from 'store/selectors/new-sportsbook';
import { GlobalIcon } from '../GlobalIcon';
import './index.less';

type Props = {
  market: Market;
  setEvent?: Function;
  teamsInfo: {
    team1: string;
    team2: string;
    startAt: number;
  };
  isUsaSinglePage: boolean;
  oddFormat: number;
  sportName?: string;
  competitionName?: string;
  gameInfo?: GameInfo & {
    type: number;
  };
  currentSport?: string;
  sport?: string;
  gameId?: number;
  isBlocked?: number;
};

const MARKETS_ROW_COUNT = 8;
const MARKETS_SHOW_LINES = 2;

export const MatchCardBase: FC<Props> = ({
  market,
  teamsInfo,
  sportName,
  competitionName,
  gameInfo,
  isBlocked,
  currentSport,
  gameId: _gameId,
  sport: _sport,
  isUsaSinglePage,
  oddFormat
}) => {
  const dispatch = useDispatch();
  const configs = useSportsbookConfigs();

  const [showAllEvents, setShowAllEvents] = useState(false);
  const selectedEventIds = useSelector(getEventIds).map(id => `${id}`);
  const sportData = useSportName();
  const quickBetEnabled = useSelector(getQuickBetEnabled);
  const { t } = useTranslation();
  const editBet = useSelector(getEditBet);

  const currentSportId = useSelector(
    getSportIdByAlias((_sport || currentSport) as string, configs.type)
  );

  // ATTENTION: POSSIBLE REFACTOR
  const {
    params: { sport = _sport || '', game }
  } = useSbRouteMatch();

  const gameId = _gameId || game;

  const orderedEvents = useMemo(() => {
    const events: Event[] = Object.values(market.event || {});

    if (events.length === 0) {
      return [];
    }

    const maxDisplayColumn: number | undefined = Math.max(
      ...events.map(event => event.display_column || 0)
    );

    if (maxDisplayColumn === 0) {
      return events.sort((a, b) => a.order - b.order);
    }

    const eventGroupsNew: Event[][] = Array.from(
      { length: (maxDisplayColumn || 0) + 1 },
      () => []
    );

    for (let i = 0, len = eventGroupsNew.length; i < len; i++) {
      eventGroupsNew[i] = events
        .filter(event => event.display_column === (i === 0 ? undefined : i))
        .sort((a, b) => a.order - b.order);
    }

    const orderedEvents: Event[] = [];

    const maxLength = Math.max(...eventGroupsNew.map(group => group.length));

    for (let i = 0; i < maxLength; i++) {
      eventGroupsNew.forEach(eventGroup => {
        eventGroup.length &&
          orderedEvents.push((eventGroup as Event[]).shift() as Event);
      });
    }

    return orderedEvents;
  }, [market.event]);

  const isCorrectScore = market.market_type?.includes('CorrectScore');

  const options = useMemo<IScoreOptions>(() => {
    return orderedEvents.map((event: Event) => ({
      value: event.id && event.id.toString(),
      coeficient: event.price,
      handicap: createOddType(event, teamsInfo, market.market_type)
    })) as IScoreOptions;
  }, [orderedEvents, teamsInfo, market.market_type]);

  const events = useMemo(() => {
    const result = isCorrectScore ? orderOptions(options) : options;

    const eventsCount = showAllEvents
      ? result.length
      : MARKETS_SHOW_LINES * (market.col_count || 0);

    if (
      result.length <= (market.col_count || 0) * MARKETS_ROW_COUNT &&
      result.length > eventsCount &&
      (isCorrectScore
        ? result.length <= MARKETS_ROW_COUNT * MARKETS_SHOW_LINES
        : true)
    ) {
      return result;
    }

    return result.slice(0, eventsCount);
  }, [market.col_count, showAllEvents, isCorrectScore, options]);

  const showToggleEventsButton = useMemo(() => {
    const result = isCorrectScore ? orderOptions(options) : options;

    return (
      (isCorrectScore ? MARKETS_SHOW_LINES : market.col_count || 0) *
        MARKETS_ROW_COUNT <
      result.length
    );
  }, [market.col_count, options, isCorrectScore]);

  const marketType = market.market_type || market.type;

  const addToBetSlip = (eventId: string | number) => {
    const event = orderedEvents.find(
      (event: Event) => event.id === Number(eventId)
    );

    let sportIndex = sport;

    if (!isNaN(Number(sport)) && sportName) {
      sportIndex = sportName.split(' ').join('');
    }

    let addedFromInlineCard = false;

    if (editBet?.active) {
      addedFromInlineCard = true;
    }

    addBetSlipEvent(
      {
        sport_id: currentSportId,
        gameId: Number(gameId),
        marketType: marketType || '',
        market: market,
        eventId: Number(eventId),
        eventBase: event?.base,
        team1: teamsInfo.team1,
        team2: teamsInfo.team2 || '',
        coeficient: event?.price,
        event_name: event?.name || '',
        game_start_date: teamsInfo.startAt,
        sport_index: (sportIndex as string) || currentSport,
        sport_name: sportData || sportName || '',
        sport_alias: '',
        competition_name: competitionName || '',
        current_game_state: gameInfo?.current_game_state || '',
        current_game_time: gameInfo?.current_game_time || '',
        isLive: gameInfo?.type === 1,
        region_name: '',
        market_name: market?.name || '',
        game_name: `${teamsInfo.team1} ${
          teamsInfo.team2 ? ` - ${teamsInfo.team2}` : ''
        }`,
        game_info: gameInfo,
        express_id: market.prematch_express_id || market.express_id,
        ewAllowed: event?.ew_allowed,
        type_1: event?.type_1
      },
      addedFromInlineCard
    );
  };

  const { oddChangeHandler, popover, hidePopover } = useOddChange(
    () => {},
    addToBetSlip,
    selectedEventIds
  );

  useEffect(() => {
    if (quickBetEnabled && selectedEventIds.length) {
      dispatch(updateEventAndGameIds([]));
    }
  }, [quickBetEnabled]);

  return (
    <>
      <div
        className={cc([
          'mathCardBaseContainer',
          {
            ['mathCardBaseContainer--withBottomPadding']:
              !showToggleEventsButton
          }
        ])}
      >
        <Odds
          marketType={market.market_type || market.type}
          value={selectedEventIds}
          isBlocked={isBlocked}
          gameId={String(gameId)}
          onChange={oddChangeHandler}
          options={events}
          marketColCount={market.col_count}
          fullName
          popover={popover}
          hidePopover={hidePopover}
          isCorrectScoreOrdered
          isUsaSinglePage={isUsaSinglePage}
          oddFormat={oddFormat}
        />
      </div>

      {showToggleEventsButton && (
        <span
          className="showSelection"
          onClick={() => setShowAllEvents(prev => !prev)}
        >
          <span>
            {t(
              `sportsbook.${
                showAllEvents ? 'showLessSelections' : 'showMoreSelections'
              }`
            )}
          </span>
          <GlobalIcon
            lib="generic"
            name="caretDownSmall"
            theme="default"
            size={EIconSize._24}
            className={showAllEvents ? 'showSelection__arrow-rotate' : ''}
          />
        </span>
      )}
    </>
  );
};
